import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import TextField from "@mui/material/TextField";

import DropdownSelector from "../../../atoms/Common/DropdownSelector.tsx";
import { LGNumber } from "../../../atoms/Inputs/LGNumber.tsx";
import {
  getCatalogue,
  getTaxRegimeList,
} from "../../../api/common/catalogues.ts";
import { addTaxRetention, getExpenseAccountList } from "../../../api/accounting";
import { useSnack } from "../../../services/SnakbarProvider";
import { handleError } from "../../../services/errorHandler";

const validationSchema = Yup.object().shape({
  providerTypeId: Yup.number()
    .integer()
    .required("El tipo de proveedor es obligatorio"),
  taxRegimeId: Yup.number()
    .integer().nullable(),
  economicActivityId: Yup.number()
    .integer().nullable(),
  ivaRate: Yup.number().min(0).required("El IVA es obligatorio"),
  isrRate: Yup.number().min(0).required("El ISR es obligatorio"),
  ivaRetentionAccountId: Yup.number()
    .integer().nullable(),
  isrRetentionAccountId: Yup.number()
    .integer().nullable(),
  description: Yup.string().nullable(),
});

export default function CreateTaxRetentionForm({ companyId, onSubmit }) {
  const { openSnack } = useSnack();

  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [selectedProviderTypeId, setSelectedProviderTypeId] = useState(null);
  const [taxRegimes, setTaxRegimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExpenseAccounts, setLoadingExpenseAccounts] = useState(false);

  const initialValues = {
    companyId: companyId ?? "",
    providerTypeId: "",
    taxRegimeId: "",
    economicActivityId: "",
    ivaRate: 0,
    isrRate: 0,
    ivaRetentionAccountId: "",
    isrRetentionAccountId: "",
    description: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        ...values,
        companyId: companyId,
        description: values.description ?? "",
      };

      const response = await addTaxRetention(payload);
      openSnack("Retención fiscal creada con éxito", "success");
      onSubmit();
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setSubmitting(false);
    }
  };


  const fetchTaxRegimes = async () => {
    if (selectedProviderTypeId == null) return;
    setLoading(true);
    try {
      const res = await getTaxRegimeList({
        providerTypeId: selectedProviderTypeId,
      });
      setTaxRegimes(res.data);
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseAccounts = async () => {
    if (!companyId) return;
    setLoadingExpenseAccounts(true);
    try {
      const res = await getExpenseAccountList({
        companyId,
        pageNumber: 1,
        pageSize: 1000,
      });
      setExpenseAccounts(res.data);
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoadingExpenseAccounts(false);
    }
  };

  useEffect(() => {
    fetchTaxRegimes();
  }, [selectedProviderTypeId]);

  useEffect(() => {
    fetchExpenseAccounts();
  }, [companyId]);

  return (
    <Box
      component="div"
      sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FastField
                  name="providerTypeId"
                  label="Tipo de proveedor"
                  as={DropdownSelector}
                  fetchFunction={getCatalogue}
                  fetchParams={{ CatalogueName: "PROVIDER_TYPE" }}
                  optionLabel={["code", "name"]}
                  optionSeparator=" | "
                  onChange={(event, value) =>
                    setSelectedProviderTypeId(value?.id ?? null)
                  }
                />
              </Grid>
              {loading ? <CircularProgress /> : (
                <>
                  <Grid item xs={12}>
                    <FastField
                      name="taxRegimeId"
                      label="Régimen fiscal"
                      as={DropdownSelector}
                      parentOptions={taxRegimes}
                      optionLabel={["code", "name"]}
                      optionSeparator=" | "
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="economicActivityId"
                      label="Actividad económica"
                      as={DropdownSelector}
                      parentOptions={expenseAccounts}
                      optionLabel={["contpaqiCode", "name"]}
                      optionSeparator=" - "
                    />
                  </Grid>
                </>
              )}

              {loadingExpenseAccounts ? <CircularProgress /> : (
                <>
                  <Grid item xs={3}>
                    <FastField
                      name="ivaRate"
                      label="Tasa IVA"
                      as={LGNumber}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={9}>
                    <FastField
                      name="ivaRetentionAccountId"
                      label="Cuenta retención IVA"
                      as={DropdownSelector}
                      parentOptions={expenseAccounts}
                      optionLabel={["contpaqiCode", "name"]}
                      optionSeparator=" - "
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <FastField
                      name="isrRate"
                      label="Tasa ISR"
                      as={LGNumber}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={9}>
                    <FastField
                      name="isrRetentionAccountId"
                      label="Cuenta retención ISR"
                      as={DropdownSelector}
                      parentOptions={expenseAccounts}
                      optionLabel={["contpaqiCode", "name"]}
                      optionSeparator=" - "
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <FastField
                  fullWidth
                  name="description"
                  label="Descripción"
                  as={TextField}
                  multiline
                  minRows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                >
                  Crear
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

